/**
 * The one text-location algorithm, shared by every docx flow.
 *
 * Two different models flatten a document into a visible string: `buildTextModel`
 * (src/lib/docx/tokenize.ts) over `word/document.xml`, and `buildBodyModel`
 * (…/docx-builder/selection/body-model.ts) over the rendered docx-preview DOM. Marking
 * happens against the DOM; tokenizing, editing and signature anchoring happen against the
 * XML. If those two disagree about WHICH occurrence of a phrase the user meant, a field
 * silently lands in the wrong place — or, at save time, refuses to land at all.
 *
 * They used to hold three near-identical copies of the matching logic, which is exactly
 * how they drifted. This module owns it once; callers supply only the policy:
 *
 *   locate()          (XML)  → refuse on ambiguity, a wrong span corrupts the document
 *   locateInBody()    (DOM)  → never refuse, a best-guess highlight beats no highlight
 *   checkPlaceable()  (DOM)  → refuse on ambiguity, and say so while the user can still act
 *
 * Deliberately dependency-free: `tokenize.ts` pulls in pizzip + @xmldom/xmldom, so the
 * rendered-DOM side must not import it or the whole XML stack lands in the marking bundle.
 */

/** Chars of stored context either side that {@link locateText} weighs. */
export const CONTEXT_WINDOW = 16;

export const ERR_EMPTY_SELECTION = 'Selection is empty';
export const ERR_NOT_FOUND = 'Selected text was not found in the document';
export const ERR_AMBIGUOUS =
  'This text appears multiple times — select a longer, unique phrase (or include more surrounding text)';

/** Collapse whitespace runs to single spaces, keeping a map from normalized index → raw index. */
export function normalize(s: string): { norm: string; map: number[] } {
  let norm = '';
  const map: number[] = [];
  let prevSpace = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (/\s/.test(ch)) {
      if (!prevSpace) {
        norm += ' ';
        map.push(i);
        prevSpace = true;
      }
    } else {
      norm += ch;
      map.push(i);
      prevSpace = false;
    }
  }
  return { norm, map };
}

export interface LocateOptions {
  /** Text immediately before the selection, as captured when it was marked. */
  contextBefore?: string;
  /** Text immediately after the selection, as captured when it was marked. */
  contextAfter?: string;
  /**
   * Which occurrence the user actually picked, recorded by {@link occurrenceAt} against the
   * model that captured the selection (the rendered DOM).
   *
   * This is the only thing that can separate text which repeats with genuinely identical
   * surroundings — the third row of three identical table rows is not distinguishable by
   * any amount of context, but the admin still clicked one specific one.
   */
  occurrenceIndex?: number | null;
  /**
   * How many occurrences the capturing model saw. The ordinal is honoured ONLY if this
   * equals the count found here: two different flattenings agreeing on the total is the
   * evidence that their occurrence sequences line up. Disagree, and the ordinal would point
   * at some other copy of the text, so it is discarded and the tie stands.
   */
  occurrenceCount?: number | null;
}

/** How the winning span was chosen — surfaced for diagnostics, not behaviour. */
export type LocateVia = 'unique' | 'context' | 'ordinal' | 'guess';

export interface LocateOutcome {
  /** Occurrences of the needle, counting overlaps. 0 when empty or absent. */
  matchCount: number;
  /** Raw (pre-normalization) offsets into the haystack; null when there is nothing to point at. */
  span: { rawStart: number; rawEnd: number } | null;
  /** True when the winning span was a coin-flip — callers decide whether that is fatal. */
  ambiguous: boolean;
  via: LocateVia;
  /** The needle normalized to nothing; distinguishes "empty" from "absent". */
  empty: boolean;
}

const NOTHING: LocateOutcome = {
  matchCount: 0,
  span: null,
  ambiguous: false,
  via: 'guess',
  empty: true,
};

/**
 * Rank of one candidate occurrence, ordered by `compareScore`.
 *
 * `tier` reproduces the original 0–4 context score over a {@link CONTEXT_WINDOW}-char
 * window, so wherever the previous implementation already picked a unique winner this one
 * picks the same. `depth` is a strictly lower-priority tiebreak, so it can only refine
 * outcomes that were already a coin-flip — never overturn a decided one.
 *
 * `depth` measures how far the stored context and the document actually agree, across the
 * FULL captured context rather than a 16-char window. Callers store ~40 chars either side;
 * throwing 24 of them away is why two rows of a signature table, identical for 16 chars and
 * different at 20, used to be declared hopelessly ambiguous.
 */
interface Score {
  tier: number;
  depth: number;
}

function compareScore(a: Score, b: Score): number {
  return a.tier !== b.tier ? b.tier - a.tier : b.depth - a.depth;
}

/** Length of the longest common suffix of two strings. */
function commonSuffixLength(a: string, b: string): number {
  let n = 0;
  while (n < a.length && n < b.length && a[a.length - 1 - n] === b[b.length - 1 - n]) n++;
  return n;
}

/** Length of the longest common prefix of two strings. */
function commonPrefixLength(a: string, b: string): number {
  let n = 0;
  while (n < a.length && n < b.length && a[n] === b[n]) n++;
  return n;
}

/** Every start index of `needle` in `haystack`, counting overlapping occurrences. */
function allMatches(haystack: string, needle: string): number[] {
  const out: number[] = [];
  let idx = haystack.indexOf(needle);
  while (idx !== -1) {
    out.push(idx);
    idx = haystack.indexOf(needle, idx + 1);
  }
  return out;
}

function scoreMatch(
  nV: string,
  m: number,
  needleLen: number,
  cb: string,
  ca: string,
  cbFull: string,
  caFull: string
): Score {
  const before = nV.slice(Math.max(0, m - cb.length), m);
  const after = nV.slice(m + needleLen, m + needleLen + ca.length);
  let tier = 0;
  if (cb) tier += before.endsWith(cb) ? 2 : before.includes(cb) ? 1 : 0;
  if (ca) tier += after.startsWith(ca) ? 2 : after.includes(ca) ? 1 : 0;

  const depth =
    commonSuffixLength(cbFull, nV.slice(Math.max(0, m - cbFull.length), m)) +
    commonPrefixLength(caFull, nV.slice(m + needleLen, m + needleLen + caFull.length));

  return { tier, depth };
}

/**
 * Find `needle` in `haystack`, both compared with whitespace collapsed, disambiguating
 * repeated occurrences by the surrounding context captured at marking time.
 *
 * Ties are reported, not resolved: `ambiguous` is true when the top two candidates score
 * identically, or when the winner has no context evidence at all. On a tie the earliest
 * occurrence is still returned so best-effort callers have something to draw.
 */
export function locateText(
  haystack: string,
  needle: string,
  opts: LocateOptions = {}
): LocateOutcome {
  const { norm: nV, map } = normalize(haystack);
  const n = normalize((needle ?? '').trim()).norm;
  if (!n) return NOTHING;

  const matches = allMatches(nV, n);
  if (matches.length === 0) {
    return { matchCount: 0, span: null, ambiguous: false, via: 'guess', empty: false };
  }

  const toSpan = (chosen: number) => ({
    rawStart: map[chosen],
    rawEnd: map[chosen + n.length - 1] + 1,
  });

  if (matches.length === 1) {
    return { matchCount: 1, span: toSpan(matches[0]), ambiguous: false, via: 'unique', empty: false };
  }

  const cbFull = normalize(opts.contextBefore ?? '').norm;
  const caFull = normalize(opts.contextAfter ?? '').norm;
  const cb = cbFull.slice(-CONTEXT_WINDOW).trimStart();
  const ca = caFull.slice(0, CONTEXT_WINDOW).trimEnd();

  const scored = matches.map((m) => ({
    m,
    score: scoreMatch(nV, m, n.length, cb, ca, cbFull, caFull),
  }));

  // Strictly-better wins only, so an all-tie keeps the earliest occurrence — the behaviour
  // best-effort callers (highlight placement, signature fallback) already depend on.
  let best = scored[0];
  let runnerUp = scored[1];
  for (const cand of scored.slice(1)) {
    if (compareScore(cand.score, best.score) < 0) {
      runnerUp = best;
      best = cand;
    } else if (runnerUp === best || compareScore(cand.score, runnerUp.score) < 0) {
      runnerUp = cand;
    }
  }

  // Only a total absence of agreement counts as "no evidence" now: a context that matches
  // partially (tier 0, depth > 0) is still evidence, and is what distinguishes occurrences
  // whose neighbourhoods diverge just outside the 16-char window.
  const noEvidence = best.score.tier === 0 && best.score.depth === 0;
  const tied = compareScore(best.score, runnerUp.score) === 0;
  const ambiguous = noEvidence || tied;

  // Context has run out. Fall back to WHICH occurrence was clicked — but only once the two
  // models have proved they agree on how many there are.
  if (ambiguous) {
    const { occurrenceIndex: oi, occurrenceCount: oc } = opts;
    if (oi != null && oc === matches.length && oi >= 0 && oi < matches.length) {
      return {
        matchCount: matches.length,
        span: toSpan(matches[oi]),
        ambiguous: false,
        via: 'ordinal',
        empty: false,
      };
    }
  }

  return {
    matchCount: matches.length,
    span: toSpan(best.m),
    ambiguous,
    via: ambiguous ? 'guess' : 'context',
    empty: false,
  };
}

/**
 * Which occurrence of `needle` sits at raw offset `rawStart`, and how many there are.
 *
 * Captured at marking time so {@link locateText} can replay the admin's actual choice when
 * context cannot tell two occurrences apart. Matching is by nearest occurrence rather than
 * exact equality: a selection may begin on whitespace that normalization folds away.
 */
export function occurrenceAt(
  haystack: string,
  needle: string,
  rawStart: number
): { index: number; count: number } | null {
  const { norm: nV, map } = normalize(haystack);
  const n = normalize((needle ?? '').trim()).norm;
  if (!n) return null;

  const matches = allMatches(nV, n);
  if (matches.length === 0) return null;

  let target = map.findIndex((raw) => raw >= rawStart);
  if (target < 0) target = map.length;

  let index = 0;
  let bestDistance = Infinity;
  matches.forEach((m, i) => {
    const distance = Math.abs(m - target);
    if (distance < bestDistance) {
      bestDistance = distance;
      index = i;
    }
  });
  return { index, count: matches.length };
}
